import * as React from 'react';
import {
  ChevronsLeft,
  ChevronsRight,
  Ghost,
  Menu,
  Monitor,
  Moon,
  MoonStar,
  Snowflake,
  Sparkles,
  Sun,
  SunMedium,
  X,
} from 'lucide-react';
import { cn } from '../lib/utils.js';
import {
  THEME_PREFERENCE_LABELS,
  useTheme,
  type ThemeName,
  type ThemePreference,
} from '../theme/theme-provider.js';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './menu.js';

export interface NavItem {
  key: string;
  label: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  /** 右肩に出す件数バッジ（ジョブ待ち・アラート数など）。 */
  badge?: number | string;
  badgeTone?: 'default' | 'warning' | 'destructive';
}

export interface NavGroup {
  key: string;
  label?: string;
  items: NavItem[];
}

/**
 * 本文（`main`）のスクロール位置をルーターに扱わせるための識別子。
 *
 * スクロールするのは window ではなく `main` なので、画面を移っても `main` の
 * 位置がそのまま残る（admin は TanStack Router の `scrollRestoration` が
 * 前の画面の位置を次の画面へ**コピーまでしていた**）。両アプリの `createRouter` は
 * `scrollToTopSelectors` にこのセレクタを入れて、新しい画面では先頭へ戻す。
 *
 * `data-scroll-restoration-id` は TanStack Router が読む属性名。付けないと
 * ルーターは `div:nth-child(2) > main:nth-child(2)` のような DOM の位置で要素を
 * 覚えるので、モバイルのドロワーが開いている間（shell の子が 1 つ増える）に
 * 記録した位置が別の要素を指す。ここはルーターを import しない（属性を付けるだけ）。
 */
export const APP_MAIN_SCROLL_ID = 'app-main';
export const APP_MAIN_SCROLL_SELECTOR = `[data-scroll-restoration-id="${APP_MAIN_SCROLL_ID}"]`;

/**
 * admin / portal の `createRouter` に**そのまま広げて渡す**スクロールの設定。
 * 両アプリで食い違わないよう、ここ 1 か所に置く。
 *
 * - `scrollRestoration`: 戻る / 進むでは、その画面を離れたときの位置
 *   （window と main の両方）へ戻す。
 * - `scrollToTopSelectors`: 新しい画面へ移ったときは main も先頭へ戻す。
 *   window は既定で先頭へ戻るが、main は名指ししないと戻らない。しかも
 *   `scrollRestoration` だけを有効にすると、ルーターは前の画面の main の位置を
 *   次の画面の記録へ**コピーする**（router-core の `setupScrollRestoration`）ので、
 *   長い一覧の下のほうから詳細へ移ると、詳細も下のほうから始まる。
 *
 * **検索パラメータだけ変える `navigate` も「新しい画面」として先頭へ戻る**（履歴の項目ごとに
 * 新しいキーが振られるため）。フィルタ・並べ替え・ページ送り・`?tab=` は結果が入れ替わるので
 * それでよい。**同じ画面の中で行を選んで下に詳細を出す・閉じる遷移には `resetScroll: false`
 * を渡す**（渡さないと選んだ行も詳細も画面外へ飛ぶ。admin の support / incidents / ai）。
 */
export const APP_SHELL_ROUTER_SCROLL_OPTIONS: {
  scrollRestoration: boolean;
  scrollToTopSelectors: string[];
} = {
  scrollRestoration: true,
  scrollToTopSelectors: ['window', APP_MAIN_SCROLL_SELECTOR],
};

/**
 * 管理コンソール / 契約者パネル共通のアプリシェル。
 *
 * - サイドバーは --sidebar-bg、本文は --background と、サーフェス階層で
 *   ナビゲーションと作業領域を分離する（aurora-mail のメールシェルと同じ考え方）。
 * - 折りたたみ状態は localStorage に保持。
 * - モバイル幅ではサイドバーをドロワーとして被せる。
 */
export function AppShell({
  brand,
  navGroups,
  currentPath,
  renderLink,
  topBarRight,
  footer,
  children,
  storageKey = 'mail-sv.sidebar.collapsed',
}: {
  brand: { name: string; sub?: string; icon?: React.ReactNode };
  navGroups: NavGroup[];
  currentPath: string;
  /** ルーターに依存しないよう、リンク描画は呼び出し側に委ねる。 */
  renderLink: (item: NavItem, content: React.ReactNode, className: string) => React.ReactNode;
  topBarRight?: React.ReactNode;
  footer?: React.ReactNode;
  children: React.ReactNode;
  storageKey?: string;
}) {
  const [collapsed, setCollapsed] = React.useState(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(storageKey) === '1';
  });
  const [drawerOpen, setDrawerOpen] = React.useState(false);

  const toggleCollapsed = React.useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(storageKey, next ? '1' : '0');
      } catch {
        /* noop */
      }
      return next;
    });
  }, [storageKey]);

  // 画面遷移したらモバイルドロワーは閉じる。
  React.useEffect(() => {
    setDrawerOpen(false);
  }, [currentPath]);

  const sidebar = (
    <Sidebar
      brand={brand}
      navGroups={navGroups}
      currentPath={currentPath}
      renderLink={renderLink}
      collapsed={collapsed}
      onToggleCollapsed={toggleCollapsed}
      footer={footer}
    />
  );

  /*
   * **文書（window）はスクロールさせず、main だけをスクロールさせる。**
   *
   * - `relative`（ここと main の 2 か所）: absolute な子孫の基準（containing block）を
   *   shell の内側に閉じ込める。main の側の理由は main の直前に書いた。
   *   ここに付けているのは header とサイドバーの中の absolute 要素のための保険。
   *   **ここだけに付けるのは不可。** はみ出した分が overflow-hidden の shell の
   *   見えないスクロール範囲に入り、focus / scrollIntoView で shell ごとずれて戻せなくなる。
   * - 高さ: `h-screen`（100vh）はモバイルではアドレスバーを隠したときの高さなので、
   *   バーが出ている間は main の下端が隠れ、文書もバーの分だけスクロールする。
   *   dvh が使えるブラウザでだけ `@supports` で 100dvh に上書きし、使えないブラウザは 100vh のまま。
   *   （`h-screen h-dvh` の併記でも今は同じ結果になる。Tailwind 3.4 は既定テーマの順に
   *   `.h-screen` → `.h-dvh` と出力し、非対応ブラウザは `100dvh` を無効として捨てるため。
   *   ここでは出力順に依存しないよう `@supports` で明示している。）
   * - 印刷（`print:`）: サイドバー・ドロワー・上部バーを消し、高さの固定と切り取りを外して
   *   本文を紙の長さぶん流す。外さないと画面 1 枚分で切れる（請求書詳細で起きていた）。
   *   `!`（important）は保険。Tailwind 3.4 は `print:` を `lg:` / `supports-[…]:` より後
   *   （最後）に出力するので、ユーティリティ同士なら無くても勝つ。アプリ側の CSS や
   *   インライン style、出力順の変化に左右されないよう付けている。
   *   以前は admin の `index.css` が `header` / `aside` 要素を**すべて**消していて、
   *   秘密の控え・設定情報シートの見出し（`<header>`）まで紙から消えていた。
   *   消す対象はここで名指しし、要素名では指さない。
   */
  return (
    <div className="relative flex h-screen w-full overflow-hidden bg-background text-foreground supports-[height:100dvh]:h-dvh print:!block print:!h-auto print:!overflow-visible">
      {/* デスクトップ: 固定サイドバー */}
      <div className="hidden lg:block print:!hidden">{sidebar}</div>

      {/* モバイル: ドロワー */}
      {drawerOpen ? (
        <div className="fixed inset-0 z-50 lg:hidden print:!hidden">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-fade-in-fast"
            onClick={() => setDrawerOpen(false)}
          />
          <div className="relative h-full w-64 animate-slide-in-right">
            {sidebar}
            <button
              type="button"
              onClick={() => setDrawerOpen(false)}
              className="absolute right-2 top-3 rounded-md p-1.5 text-muted-foreground hover:bg-hover"
              aria-label="メニューを閉じる"
            >
              <X className="size-4" />
            </button>
          </div>
        </div>
      ) : null}

      <div className="flex min-w-0 flex-1 flex-col print:!block">
        <header className="flex h-14 shrink-0 items-center gap-2 border-b border-border bg-panel px-3 sm:px-4 print:!hidden">
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            className="rounded-md p-2 text-muted-foreground transition-colors hover:bg-hover hover:text-foreground lg:hidden"
            aria-label="メニューを開く"
          >
            <Menu className="size-4" />
          </button>
          <div className="min-w-0 flex-1" />
          <div className="flex items-center gap-1.5">
            {topBarRight}
            <ThemeToggle />
          </div>
        </header>

        {/*
         * **`relative` を外さない。** 無いと main の中の absolute 要素（`sr-only`、
         * `<form>` の中の Radix Select / Switch が描く非表示の select / input）の基準が
         * ページ全体になり、main のスクロールも切り取りもすり抜けて、ページ下部の
         * 静的位置で文書の高さを伸ばす。スクロールすると shell ごと上へ流れ、
         * その下に背景だけの領域が出る（2026-09-28 に /branding で発生）。
         * `data-scroll-restoration-id` は `APP_MAIN_SCROLL_ID` の説明を参照。
         */}
        <main
          data-scroll-restoration-id={APP_MAIN_SCROLL_ID}
          className="relative min-h-0 flex-1 overflow-y-auto print:!overflow-visible"
        >
          <div className="mx-auto w-full max-w-[1600px] px-4 py-6 sm:px-6 animate-fade-in">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}

function Sidebar({
  brand,
  navGroups,
  currentPath,
  renderLink,
  collapsed,
  onToggleCollapsed,
  footer,
}: {
  brand: { name: string; sub?: string; icon?: React.ReactNode };
  navGroups: NavGroup[];
  currentPath: string;
  renderLink: (item: NavItem, content: React.ReactNode, className: string) => React.ReactNode;
  collapsed: boolean;
  onToggleCollapsed: () => void;
  footer?: React.ReactNode;
}) {
  return (
    <aside
      className={cn(
        'flex h-full flex-col border-r border-border bg-sidebar-bg transition-[width] duration-200',
        collapsed ? 'w-16' : 'w-64',
      )}
    >
      <div className="flex h-14 shrink-0 items-center gap-2.5 border-b border-border px-3">
        <div className="brand-gradient flex size-8 shrink-0 items-center justify-center rounded-lg text-white shadow-lg shadow-primary/25">
          {brand.icon ?? <span className="text-sm font-bold">A</span>}
        </div>
        {!collapsed ? (
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold leading-tight">{brand.name}</p>
            {brand.sub ? (
              <p className="truncate text-[11px] leading-tight text-muted-foreground">{brand.sub}</p>
            ) : null}
          </div>
        ) : null}
      </div>

      <nav className="min-h-0 flex-1 space-y-4 overflow-y-auto px-2 py-3">
        {navGroups.map((group) => (
          <div key={group.key} className="space-y-1">
            {group.label && !collapsed ? (
              <p className="px-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                {group.label}
              </p>
            ) : null}
            {group.items.map((item) => {
              const active =
                currentPath === item.href ||
                (item.href !== '/' && currentPath.startsWith(`${item.href}/`));
              const Icon = item.icon;
              const className = cn(
                'group relative flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm font-medium transition-colors',
                active
                  ? 'bg-selected/15 text-foreground'
                  : 'text-muted-foreground hover:bg-hover hover:text-foreground',
                collapsed && 'justify-center px-0',
              );
              const content = (
                <>
                  {active ? (
                    <span className="absolute inset-y-1 left-0 w-0.5 rounded-full bg-primary" />
                  ) : null}
                  <Icon className="size-4 shrink-0" />
                  {!collapsed ? (
                    <>
                      <span className="min-w-0 flex-1 truncate">{item.label}</span>
                      {item.badge !== undefined && item.badge !== 0 ? (
                        <span
                          className={cn(
                            'shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-semibold tabular',
                            item.badgeTone === 'destructive'
                              ? 'bg-destructive/20 text-destructive'
                              : item.badgeTone === 'warning'
                                ? 'bg-warning/20 text-warning'
                                : 'bg-primary/20 text-primary',
                          )}
                        >
                          {item.badge}
                        </span>
                      ) : null}
                    </>
                  ) : null}
                </>
              );
              return (
                <React.Fragment key={item.key}>
                  {renderLink(item, content, className)}
                </React.Fragment>
              );
            })}
          </div>
        ))}
      </nav>

      <div className="shrink-0 border-t border-border p-2">
        {!collapsed && footer ? <div className="mb-2 px-1">{footer}</div> : null}
        <button
          type="button"
          onClick={onToggleCollapsed}
          className={cn(
            'flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-xs text-muted-foreground transition-colors hover:bg-hover hover:text-foreground',
            collapsed && 'justify-center px-0',
          )}
          aria-label={collapsed ? 'サイドバーを展開' : 'サイドバーを折りたたむ'}
        >
          {collapsed ? <ChevronsRight className="size-4" /> : <ChevronsLeft className="size-4" />}
          {!collapsed ? <span>折りたたむ</span> : null}
        </button>
      </div>
    </aside>
  );
}

/**
 * アイコンは**選択肢（preference）ごと**に持つ。
 * `system` は「OS に合わせる」なので、画面の見た目そのものではなく
 * 端末を表す `Monitor` を当てる。
 */
const THEME_ICON: Record<ThemePreference, React.ComponentType<{ className?: string }>> = {
  system: Monitor,
  light: Sun,
  dark: Moon,
  aurora: Sparkles,
  midnight: Moon,
  nord: Snowflake,
  dracula: Ghost,
  'solarized-dark': MoonStar,
  'solarized-light': SunMedium,
};

export function ThemeToggle() {
  const { theme, preference, preferences, setTheme } = useTheme();
  // ボタンには「今どう見えているか」ではなく「何を選んでいるか」を出す。
  // OS 追従を選んでいるのにダークのアイコンが出ると、追従が切れたように見える。
  const Icon = THEME_ICON[preference];
  void (theme satisfies ThemeName);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="rounded-md p-2 text-muted-foreground transition-colors hover:bg-hover hover:text-foreground"
          aria-label="テーマを変更"
        >
          <Icon className="size-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel>テーマ</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {preferences.map((t) => {
          const I = THEME_ICON[t];
          return (
            <DropdownMenuItem key={t} onSelect={() => setTheme(t)}>
              <I className="size-4" />
              <span className="flex-1">{THEME_PREFERENCE_LABELS[t]}</span>
              {preference === t ? <span className="text-primary">●</span> : null}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
